import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useAuth } from "@/context/AuthContext";

/**
 * Guards a route by a named feature flag. Admins ALWAYS get through.
 *
 * Redirect rules (in order):
 *   1. While auth or feature-flags are still loading       → spinner
 *   2. User is admin OR the flag is enabled                → render children
 *   3. We've waited 800ms with `user=null` post-auth-load  → redirect to /login
 *      (the route requires authentication to be useful)
 *   4. Otherwise (regular user, flag off)                  → redirect to /
 *
 * The 800ms grace period prevents a hard-reload flicker where AuthContext
 * briefly reports `user=null` while the /auth/me request is still in flight.
 */
export function FeatureGate({ flag, children }) {
  const { flags, isAdmin, loading } = useFeatureFlags();
  const { user, loading: authLoading } = useAuth();
  const [graceExpired, setGraceExpired] = useState(false);
  const timerRef = useRef(null);

  // Once everything has finished loading and the user is still null, start a
  // short grace window before redirecting — covers any final state hiccup.
  useEffect(() => {
    if (!loading && !authLoading && !user) {
      timerRef.current = setTimeout(() => setGraceExpired(true), 800);
      return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }
    setGraceExpired(false);
  }, [loading, authLoading, user]);

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]" data-testid="feature-gate-loading">
        <Loader2 className="w-7 h-7 animate-spin text-primary" />
      </div>
    );
  }

  // Admin always wins.
  if (isAdmin) return children;
  // Flag enabled — let regular users in.
  if (flags?.[flag]) return children;
  // No user at all → send to login (after grace).
  if (!user && graceExpired) return <Navigate to="/login" replace />;
  // Logged in but flag off → home.
  if (user) return <Navigate to="/" replace />;
  // Still resolving — keep showing the spinner instead of redirecting.
  return (
    <div className="flex items-center justify-center min-h-[300px]" data-testid="feature-gate-loading">
      <Loader2 className="w-7 h-7 animate-spin text-primary" />
    </div>
  );
}

export default FeatureGate;
